import '../styles/global.css';
import Head from 'next/head';
import { AppProps } from 'next/app';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { datadogRum } from '@datadog/browser-rum';
import { CurrencyProvider } from '../contexts/CurrencyContext';
import defaultTheme from '../styles/theme';
import createEmotionCache from '../util/createEmotionCache';
import { MainLayout } from '../components/MainLayout';
import packageJson from 'package.json';
import { AuthProvider } from '../contexts/AuthContext';
import { useEffect } from 'react';
import { WagmiConfig, createConfig, configureChains } from 'wagmi';
// import { publicProvider } from 'wagmi/providers/public';
// import { alchemyProvider } from 'wagmi/providers/alchemy';
// import { jsonRpcProvider } from 'wagmi/providers/jsonRpc';
import { TwitchAuthProvider } from 'src/contexts/TwitchAuthContext';
import { GlobalComponentsProvider } from 'src/contexts/GlobalComponentsContext';
import {
  mainnet,
  goerli,
  polygon,
  avalanche,
  avalancheFuji,
  arbitrum,
  arbitrumGoerli,
  bsc,
  bscTestnet,
  fantom,
  fantomTestnet,
  optimism,
  optimismGoerli,
  polygonMumbai,
} from 'wagmi/chains';
import { ServerTimeProvider } from 'src/contexts/useServerTime';
import { LocationProvider } from 'src/contexts/useLocation';
import {
  EthereumClient,
  w3mConnectors,
  w3mProvider,
} from '@web3modal/ethereum';
import { Web3Modal } from '@web3modal/react';
import { PageLoadingProvider } from 'src/contexts/PageLoadingContext';
import { MaintenanceMode } from 'src/components/MaintenanceMode';

const defaultChains = [
  arbitrum,
  arbitrumGoerli,
  // aurora,
  // auroraTestnet,
  avalanche,
  avalancheFuji,
  // bronos,
  // bronosTestnet,
  bsc,
  bscTestnet,
  // canto,
  // celo,
  // celoAlfajores,
  // crossbell,
  // evmos,
  // evmosTestnet,
  fantom,
  fantomTestnet,
  // filecoin,
  // filecoinCalibration,
  // filecoinHyperspace,
  // foundry,
  // gnosis,
  // gnosisChiado,
  goerli,
  // hardhat,
  // iotex,
  // iotexTestnet,
  // localhost,
  mainnet,
  // metis,
  // metisGoerli,
  // moonbaseAlpha,
  // moonbeam,
  // moonriver,
  // okc,
  optimism,
  optimismGoerli,
  polygon,
  polygonMumbai,
  // sepolia,
  // taraxa,
  // taraxaTestnet,
  // telos,
  // telosTestnet,
  // zkSync,
  // zkSyncTestnet,
];

const { publicClient } = configureChains(defaultChains, [
  // jsonRpcProvider({
  //   rpc: (chain) => ({
  //     http: `https://eth-${chain.network}.g.alchemy.com/v2/j_ZagcOwyY1RbDyICUH-GjQh-Swdp6Xe`,
  //     webSocket: `wss://eth-${chain.network}.g.alchemy.com/v2/j_ZagcOwyY1RbDyICUH-GjQh-Swdp6Xe`,
  //   }),
  //   priority: 0,
  // }),
  w3mProvider({
    projectId: process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID!,
  }),
  // alchemyProvider({ apiKey: 'j_ZagcOwyY1RbDyICUH-GjQh-Swdp6Xe', priority: 1 }),
  // publicProvider({ priority: 2 }),
]);

const wagmiConfig = createConfig({
  publicClient,
  autoConnect: true,
  connectors: w3mConnectors({
    projectId: process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID!,
    version: 1, // TODO: Upgrade to v2 once 90% of wallets are supported https://explorer.walletconnect.com/?type=wallet&version=1
    chains: defaultChains,
  }),
});

const ethereumClient = new EthereumClient(wagmiConfig, defaultChains);

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

datadogRum.init({
  applicationId: 'bfd73982-b1b5-493f-b203-f53e583c943d',
  clientToken: 'pub7f0076c8d4577a4a675f975798b1f5c2',
  site: 'datadoghq.com',
  service: 'blumint-frontend',
  env: process.env.NODE_ENV,
  // Specify a version number to identify the deployed version of your application in Datadog
  version: packageJson.version,
  sampleRate: 100,
  premiumSampleRate: 100,
  trackInteractions: true,
  trackFrustrations: true,
  defaultPrivacyLevel: 'mask-user-input',
});

datadogRum.startSessionReplayRecording();

type BlumintAppProps = AppProps & {
  emotionCache?: EmotionCache;
};

export default function BlumintApp(props: BlumintAppProps) {
  function registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('./firebase-messaging-sw.js')
        .then(function (registration: ServiceWorkerRegistration) {
          console.log('Registration successful, scope is:', registration.scope);
        })
        .catch(function (err: Error) {
          console.log('Service worker registration failed, error:', err);
        });
    }
  }
  useEffect(() => {
    registerServiceWorker();
    if (screen.width > 654) {
      //document.body.style.zoom = '90%';
    }
  }, []);

  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

  return (
    <WagmiConfig config={wagmiConfig}>
      <LocationProvider>
        <ServerTimeProvider>
          <TwitchAuthProvider>
            <ThemeProvider theme={defaultTheme}>
              <CssBaseline />
              <MaintenanceMode>
                <PageLoadingProvider>
                  <AuthProvider>
                    <CurrencyProvider>
                      <CacheProvider value={emotionCache}>
                        <Head>
                          <meta name="viewport" content="width=654" />
                        </Head>
                        <GlobalComponentsProvider>
                          <MainLayout>
                            <Component {...pageProps} />
                            <Web3Modal
                              projectId={
                                process.env
                                  .NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID!
                              }
                              ethereumClient={ethereumClient}
                              // TODO: Cannot set defaultChain due to the following issue - https://discord.com/channels/492410046307631105/1087308968847753276 - defaultChain={polygon}
                              // Also waiting on https://discord.com/channels/492410046307631105/1087104193145344212
                              themeVariables={{
                                '--w3m-z-index': '2000',
                              }}
                            />
                          </MainLayout>
                        </GlobalComponentsProvider>
                      </CacheProvider>
                    </CurrencyProvider>
                  </AuthProvider>
                </PageLoadingProvider>
              </MaintenanceMode>
            </ThemeProvider>
          </TwitchAuthProvider>
        </ServerTimeProvider>
      </LocationProvider>
    </WagmiConfig>
  );
}
